import { createContext, useState, useContext, useEffect, useRef } from "react";
import { useCookies } from "react-cookie";

import { useUserContext } from "./UserContext";
import { getMyCart, saveMyCart } from "../services/UserServices";

const CartContext = createContext();

// Cart items: { id, amount, size, price } where price is the line total
const clean = (cart) => (Array.isArray(cart) ? cart : []).filter(item => item && item.id && item.amount > 0);

// Guest cart + saved account cart: keep every product once, with the larger quantity
const mergeCarts = (server, local) => {
    const byId = {};
    [...server, ...local].forEach(item => {
        const prev = byId[item.id];
        if (!prev || item.amount > prev.amount) {
            const unit = item.price / item.amount || (prev && prev.price / prev.amount) || 0;
            byId[item.id] = { id: item.id, amount: item.amount, size: item.size || prev?.size || '', price: unit * item.amount };
        } else if (!prev.size && item.size) {
            prev.size = item.size;
        }
    });
    return Object.values(byId);
};

export const CartProvider = ({ children }) => {

    const { currentUser } = useUserContext();
    const [cookies, setCookie, removeCookie] = useCookies(['cart']);
    const [cart, setCart] = useState(() => clean(cookies.cart));
    const [refresh, setRefresh] = useState(false);

    const cartRef = useRef(cart);
    cartRef.current = cart;
    const syncedFor = useRef(null);   // user whose saved cart is loaded
    const lastSaved = useRef('');
    // null = first render (page load), '' = logged out, id = logged in
    const previousUser = useRef(null);

    // Device copy of the cart (guests rely on it; logged-in users get it as a fast reload cache)
    useEffect(() => {
        const items = clean(cart);
        items.length ? setCookie('cart', items, { path: '/' }) : removeCookie('cart', { path: '/' });
        // `refresh` covers components that edit cart items in place
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart, refresh]);

    // Log in: load the account's cart and merge in anything added as a guest.
    // Log out: clear the cart from this device so the next person doesn't see it.
    useEffect(() => {
        const wasLoggedIn = previousUser.current;
        previousUser.current = currentUser;

        if (!currentUser) {
            syncedFor.current = null;
            lastSaved.current = '';
            if (wasLoggedIn) setCart([]);
            return;
        }

        let cancelled = false;
        getMyCart()
            .then(({ cart: saved }) => {
                if (cancelled) return;
                // Only a real guest → logged-in transition merges the device cart. On a page reload
                // the device cart is just a cache of this account's cart, and after switching
                // accounts it belongs to someone else, so in both cases the saved cart wins.
                const local = wasLoggedIn === '' ? clean(cartRef.current) : [];
                syncedFor.current = currentUser;
                setCart(mergeCarts(clean(saved), local));
            })
            .catch(() => { });
        return () => { cancelled = true; };
    }, [currentUser]);

    // Save changes to the account (debounced)
    useEffect(() => {
        if (!currentUser || syncedFor.current !== currentUser) return;
        const payload = JSON.stringify(clean(cart).map(({ id, amount, size }) => ({ id, amount, size: size || '' })));
        if (payload === lastSaved.current) return;
        const timer = setTimeout(() => {
            saveMyCart(JSON.parse(payload)).then(() => { lastSaved.current = payload; }).catch(() => { });
        }, 400);
        return () => clearTimeout(timer);
    }, [cart, refresh, currentUser]);

    // Add `qty` of a product (one line per product; a new size replaces the old one)
    const addItem = (product, size = '', qty = 1) => {
        setCart(prev => {
            const existing = prev.find(item => item.id === product._id);
            if (!existing) return [...prev, { id: product._id, amount: qty, size, price: product.price * qty }];
            return prev.map(item => {
                if (item.id !== product._id) return item;
                const amount = item.amount + qty;
                return { ...item, amount, size: size || item.size, price: product.price * amount };
            });
        });
    };

    // Change a line's quantity, keeping its unit price; 0 removes the line
    const setItemAmount = (id, amount) => {
        setCart(prev => amount <= 0
            ? prev.filter(item => item.id !== id)
            : prev.map(item => item.id === id ? { ...item, amount, price: (item.price / item.amount) * amount } : item));
    };
    const removeItem = (id) => setCart(prev => prev.filter(item => item.id !== id));
    const clearCart = () => setCart([]);

    const items = clean(cart);
    const itemCount = items.reduce((sum, item) => sum + item.amount, 0);
    const subtotal = items.reduce((sum, item) => sum + (item.price || 0), 0);

    const values = {
        cart,
        setCart,
        items,
        itemCount,
        subtotal,
        addItem,
        setItemAmount,
        removeItem,
        clearCart,
        refresh,
        setRefresh
    }

    return <CartContext.Provider value={values} >{children}</CartContext.Provider>
};

export const useCartContext = () => useContext(CartContext);
